import { Resource } from '@nexla/@types';
import { RESOURCE_TYPE_SINK } from 'js/constants/resourceTypes';
import UIUtils from 'js/lib/UIUtils';
import pick from 'lodash/pick';

type FlowTrigger = {
  triggering_event_type: string;
  data_sink_id?: number;
  data_source_id?: number;
};

export const FlowTrigger = {
  SOURCE: 'triggers-source',
  SINK: 'triggers-sink',
  EXTERNAL: 'triggers-external',
};

export const FlowTriggerEventType = {
  SOURCE: 'DATA_SOURCE_READ_DONE',
  SINK: 'DATA_SINK_WRITE_DONE',
};

const createDataSinkWriteTrigger = (data_sink_id: number): FlowTrigger => ({
  data_sink_id,
  triggering_event_type: FlowTriggerEventType.SINK,
});

const createDataSourceWriteTrigger = (data_source_id: number): FlowTrigger => ({
  data_source_id,
  triggering_event_type: FlowTriggerEventType.SOURCE,
});

const createWriteTrigger = (resourceType: string) => {
  return resourceType === RESOURCE_TYPE_SINK
    ? createDataSinkWriteTrigger
    : createDataSourceWriteTrigger;
};

/**
 * A source's `flow_triggers` (API) holds BOTH directions: triggers that start this
 * source (inbound) and triggers where this source starts another flow (outbound).
 * Only inbound ones are this source's to edit. Direction is decided by node ids, as
 * the API does — resource ids alone collide across types (a sink can share a source's
 * numeric id). Picker-built triggers (`data_sink_id` / `data_source_id`) are always inbound.
 */
const isInboundTrigger = (trigger: any, source: Resource) =>
  trigger?.triggered_origin_node_id == null ||
  trigger.triggered_origin_node_id === (source?.origin_node_id ?? source?.flow_node_id);

/** Triggers that start `source` — what decides how it is scheduled. */
const getInboundTriggers = (source: any) =>
  (source?.flow_triggers ?? []).filter((trigger) => isInboundTrigger(trigger, source));

/** Resource that fires the trigger, for either shape (API or picker-built). */
const getTriggeringResourceId = (trigger: any): number =>
  trigger?.triggering_resource_id ?? trigger?.data_sink_id ?? trigger?.data_source_id ?? 0;

const triggerKey = (trigger: any) =>
  `${trigger?.triggering_event_type}:${getTriggeringResourceId(trigger)}`;

/** Inbound triggers of either shape → the create-input shape the API's PUT/POST accept. */
const toTriggerInputs = (triggers: any[] = [], source: Resource): FlowTrigger[] =>
  (triggers ?? [])
    .filter((trigger) => isInboundTrigger(trigger, source))
    .map((trigger) =>
      trigger.triggering_event_type === FlowTriggerEventType.SOURCE
        ? createDataSourceWriteTrigger(getTriggeringResourceId(trigger))
        : createDataSinkWriteTrigger(getTriggeringResourceId(trigger)),
    );

const extractFlowTriggerFromSource = (source: Resource) =>
  toTriggerInputs(source?.flow_triggers, source);

/**
 * What to change so `source`'s inbound triggers become `desired` (either shape; outbound
 * entries are ignored). Unchanged triggers are left alone so they keep their id and
 * ACTIVE/PAUSED status; outbound triggers are never touched.
 */
const planTriggerSync = (source: Resource, desired: any[]) => {
  const current = (source?.flow_triggers ?? []).filter((t) => isInboundTrigger(t, source));
  const wanted = toTriggerInputs(desired, source);
  const currentKeys = new Set(current.map(triggerKey));
  const wantedKeys = new Set(wanted.map(triggerKey));

  return {
    toAdd: wanted.filter(
      (t, i) =>
        !currentKeys.has(triggerKey(t)) &&
        wanted.findIndex((w) => triggerKey(w) === triggerKey(t)) === i,
    ),
    toDelete: current.filter((t) => !wantedKeys.has(triggerKey(t))).map((t) => t.id),
  };
};

const getTriggers = (triggers = [], sources: any, sinks: any) => {
  return triggers.map((trigger) => {
    if (trigger.triggering_event_type === FlowTriggerEventType.SINK) {
      return {
        isSink: true,
        ...sinks[trigger?.data_sink_id || trigger.triggering_resource_id],
      };
    }
    return {
      isSource: true,
      ...sources[trigger?.data_source_id || trigger.triggering_resource_id],
    };
  });
};

const getDescriptionTrigger = (type: string) => {
  if (type === FlowTrigger.SINK) {
    return 'Trigger Source ingestion when a Destination finishes.';
  } else if (type === FlowTrigger.SOURCE) {
    return 'Trigger Source ingestion when a Source finishes.';
  } else if (type === FlowTrigger.EXTERNAL) {
    return 'Trigger Source ingestion when the external trigger fires.';
  }
};

const getDefaultOnceCron = () => {
  const currentDateAndTime = UIUtils.getCurrentDateAndTime('Once');

  return UIUtils.getCron(currentDateAndTime);
};

/**
 * MonitoringInterval reports one field per onConfigChanged call and carries the schedule
 * mode's other fields (flow_triggers, adaptive_flow) in `data`. A handler that applies only
 * (name, value) must merge these too, or switching to a schedule keeps the old triggers:
 * the config still holds the source's API triggers, which the save reads as untouched.
 */
const pickScheduleFields = (data: any) => pick(data ?? {}, ['flow_triggers', 'adaptive_flow']);

/**
 * @DOC
 * This module provides a factory for creating flow triggers for data sinks and data sources.
 */
export const FlowTriggerHelper = {
  getTriggers,
  getDescriptionTrigger,
  createWriteTrigger,
  extractFlowTriggerFromSource,
  isInboundTrigger,
  getInboundTriggers,
  getTriggeringResourceId,
  planTriggerSync,
  pickScheduleFields,
  getDefaultOnceCron,
};
